import React from 'react';

let publishMessage = () => () => undefined;
let destroyMessage = () => undefined;
let publishNotification = () => () => undefined;
let closeNotification = () => undefined;
let destroyNotification = () => undefined;

const nextId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeContent = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeContent(item)).join('');
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (React.isValidElement(value)) {
    const childContent = value.props?.children;
    return normalizeContent(childContent);
  }

  return String(value);
};

const createCloseHandle = (closer, key) => {
  const close = () => closer(key);
  close.then = (resolve) => Promise.resolve(true).then(resolve);
  close.promise = Promise.resolve(true);
  return close;
};

export const appMessage = {
  open(config) {
    const key = config?.key ?? nextId();
    publishMessage({
      key,
      type: config?.type ?? 'info',
      content: normalizeContent(config?.content),
      duration: config?.duration,
      onClose: config?.onClose,
    });
    return createCloseHandle(destroyMessage, key);
  },
  success(content, duration, onClose) {
    return appMessage.open({ type: 'success', content, duration, onClose });
  },
  error(content, duration, onClose) {
    return appMessage.open({ type: 'error', content, duration, onClose });
  },
  info(content, duration, onClose) {
    return appMessage.open({ type: 'info', content, duration, onClose });
  },
  warning(content, duration, onClose) {
    return appMessage.open({ type: 'warning', content, duration, onClose });
  },
  warn(content, duration, onClose) {
    return appMessage.warning(content, duration, onClose);
  },
  destroy(key) {
    destroyMessage(key);
  },
};

export const appNotification = {
  open(config) {
    const key = config?.key ?? nextId();
    publishNotification({
      key,
      type: config?.type ?? 'info',
      message: normalizeContent(config?.message),
      description: normalizeContent(config?.description),
      duration: config?.duration,
      onClose: config?.onClose,
    });
    return createCloseHandle(closeNotification, key);
  },
  success(config) {
    return appNotification.open({ ...config, type: 'success' });
  },
  error(config) {
    return appNotification.open({ ...config, type: 'error' });
  },
  info(config) {
    return appNotification.open({ ...config, type: 'info' });
  },
  warning(config) {
    return appNotification.open({ ...config, type: 'warning' });
  },
  warn(config) {
    return appNotification.warning(config);
  },
  close(key) {
    closeNotification(key);
  },
  destroy(key) {
    destroyNotification(key);
  },
};

const messageTypeStyles = {
  success: { borderLeft: '4px solid #3a8f45' },
  error: { borderLeft: '4px solid #c84444' },
  info: { borderLeft: '4px solid #1b67a5' },
  warning: { borderLeft: '4px solid #d38b22' },
};

const notificationTypeStyles = {
  success: { borderLeft: '4px solid #3a8f45' },
  error: { borderLeft: '4px solid #c84444' },
  info: { borderLeft: '4px solid #1b67a5' },
  warning: { borderLeft: '4px solid #d38b22' },
};

export const AntdFeedbackProvider = ({ children }) => {
  const [messages, setMessages] = React.useState([]);
  const [notifications, setNotifications] = React.useState([]);

  const removeMessage = React.useCallback((key) => {
    setMessages((current) => {
      const target = current.find((item) => item.key === key);
      if (target?.onClose) {
        target.onClose();
      }
      return current.filter((item) => item.key !== key);
    });
  }, []);

  const removeNotification = React.useCallback((key) => {
    setNotifications((current) => {
      const target = current.find((item) => item.key === key);
      if (target?.onClose) {
        target.onClose();
      }
      return current.filter((item) => item.key !== key);
    });
  }, []);

  React.useEffect(() => {
    publishMessage = (config) => {
      const duration = config.duration ?? 3;
      setMessages((current) => [...current.filter((item) => item.key !== config.key), config]);
      if (duration > 0) {
        window.setTimeout(() => removeMessage(config.key), duration * 1000);
      }
      return createCloseHandle(removeMessage, config.key);
    };

    destroyMessage = (key) => {
      if (key) {
        removeMessage(key);
        return;
      }
      setMessages([]);
    };

    publishNotification = (config) => {
      const duration = config.duration ?? 4.5;
      setNotifications((current) => [...current.filter((item) => item.key !== config.key), config]);
      if (duration > 0) {
        window.setTimeout(() => removeNotification(config.key), duration * 1000);
      }
      return createCloseHandle(removeNotification, config.key);
    };

    closeNotification = (key) => {
      if (key) {
        removeNotification(key);
      }
    };

    destroyNotification = (key) => {
      if (key) {
        removeNotification(key);
        return;
      }
      setNotifications([]);
    };

    return () => {
      publishMessage = () => () => undefined;
      destroyMessage = () => undefined;
      publishNotification = () => () => undefined;
      closeNotification = () => undefined;
      destroyNotification = () => undefined;
    };
  }, [removeMessage, removeNotification]);

  return (
    <>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          pointerEvents: 'none',
        }}
      >
        {messages.map((item) => (
          <div
            key={item.key}
            style={{
              minWidth: 240,
              maxWidth: 420,
              background: '#ffffff',
              color: '#1f1f1f',
              borderRadius: 10,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.16)',
              padding: '12px 16px',
              fontSize: 14,
              fontWeight: 500,
              pointerEvents: 'auto',
              ...messageTypeStyles[item.type],
            }}
          >
            {item.content}
          </div>
        ))}
      </div>
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          pointerEvents: 'none',
        }}
      >
        {notifications.map((item) => (
          <div
            key={item.key}
            style={{
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              background: '#ffffff',
              color: '#1f1f1f',
              borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
              padding: '14px 16px',
              pointerEvents: 'auto',
              ...notificationTypeStyles[item.type],
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: item.description ? 6 : 0 }}>
              {item.message}
            </div>
            {item.description ? (
              <div style={{ fontSize: 13, lineHeight: 1.45, color: '#4a4a4a' }}>
                {item.description}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
};
